import React, { useState, useEffect } from "react";
import { Form, Input, Button, Card, Space, message, Modal, Segmented, DatePicker, InputNumber, Row, Col } from "antd";
import { ArrowLeftOutlined, FileTextOutlined, EditOutlined, TeamOutlined, ClockCircleOutlined, SendOutlined, ExperimentOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useAppSelector } from "../../../store";
import pushBroadcastApi from "../../../api/push-broadcast-api";
import PushAudienceSelector from "./push-audience-selector";
import PushRichOptions from "./push-rich-options";
import PushLivePreview from "./push-live-preview";
import PushTemplateControls from "./push-template-controls";
import { buildAudience, buildRichOptions, buildPayload, contentToFields, asObject } from "./push-form-helpers";

const { TextArea } = Input;

const QUICK_PATHS = [
    { label: "Shopee", path: "/shopee" },
    { label: "Đơn hàng", path: "/orders" },
    { label: "Rút tiền", path: "/withdraw" },
    { label: "Vouchers", path: "/vouchers" },
    { label: "Bảng XH", path: "/leaderboard" },
    { label: "Thông báo", path: "/notifications" },
    { label: "Trang chủ", path: "/" },
];

function PushBroadcastForm() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const auth = useAppSelector((state) => state.auth);
    const [form] = Form.useForm();

    const [audienceMode, setAudienceMode] = useState("all");
    // Cách đẩy tin. "topic" chỉ hợp lệ khi gửi TẤT CẢ (BE cũng chặn) — xem
    // backend/src/services/push-send-mode.js.
    const [deliveryMode, setDeliveryMode] = useState("queue");
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [rawIds, setRawIds] = useState("");
    const [criteria, setCriteria] = useState({});
    const [previewResult, setPreviewResult] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [sendMode, setSendMode] = useState("now");
    const [scheduleTime, setScheduleTime] = useState(null);
    const [testOpen, setTestOpen] = useState(false);
    const [testUserId, setTestUserId] = useState(null);
    const [testLoading, setTestLoading] = useState(false);

    const watched = Form.useWatch([], form) || {};

    // Resend/duplicate: prefill nội dung + đối tượng từ broadcast cũ (?from=id).
    useEffect(() => {
        const from = searchParams.get("from");
        if (!from) return;
        (async () => {
            try {
                const { data } = await pushBroadcastApi.getBroadcast(from);
                const b = data.data.broadcast;
                form.setFieldsValue(contentToFields(b));
                seedAudience(b.audiencePayload);
            } catch {
                message.error("Không tải được dữ liệu để gửi lại");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Nạp sẵn danh sách userId + nội dung gợi ý từ màn khác điều hướng sang
    // (vd tab "Người tham gia" của Chương trình thưởng → nhắc nhóm chưa mua).
    // Chỉ điền form, KHÔNG tự gửi: admin vẫn xem preview và sửa nội dung.
    useEffect(() => {
        const seed = location.state;
        if (!seed?.audienceUserIds?.length) return;
        setAudienceMode("ids");
        setRawIds(seed.audienceUserIds.join("\n"));
        if (seed.preset) form.setFieldsValue(seed.preset);
        // Xoá state để F5 không nạp lại danh sách cũ.
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const seedAudience = (raw) => {
        const payload = asObject(raw);
        const mode = payload.mode;
        if (!mode) return;
        if (mode === "roles") { setAudienceMode("roles"); setSelectedRoles(payload.roles || []); }
        else if (mode === "ids") { setAudienceMode("ids"); setRawIds((payload.userIds || []).join("\n")); }
        else if (mode === "single") { setAudienceMode("ids"); setRawIds(String(payload.userId || "")); }
        else if (mode === "criteria") {
            setAudienceMode("criteria");
            setCriteria({ platform: payload.platform, active: payload.active != null ? String(payload.active) : undefined, joinedAfter: payload.joinedAfter, joinedBefore: payload.joinedBefore });
        } else setAudienceMode("all");
    };

    const handleCriteriaChange = (key, value) => setCriteria((prev) => ({ ...prev, [key]: value }));
    const handleQuickPath = (path) => form.setFieldsValue({ path, url: undefined });
    const getAudience = () => buildAudience(audienceMode, selectedRoles, rawIds, criteria);

    // Đổi đối tượng khỏi "Tất cả" mà vẫn giữ "topic" thì BE trả 400. Tự hạ về queue
    // để admin không bấm Gửi rồi mới ăn lỗi.
    const handleAudienceModeChange = (next) => {
        setAudienceMode(next);
        if (next !== "all") setDeliveryMode("queue");
    };

    const handlePreview = async () => {
        try {
            setPreviewLoading(true);
            const { data } = await pushBroadcastApi.preview(getAudience());
            setPreviewResult(data.data);
        } catch {
            message.error("Không thể lấy số lượng user");
        } finally {
            setPreviewLoading(false);
        }
    };

    const collectContent = () => {
        const v = form.getFieldsValue();
        if (!v.title || !v.message) return null;
        return { title: v.title, message: v.message, path: v.path, url: v.url, ...buildRichOptions(v) };
    };

    const handleTest = async () => {
        const v = form.getFieldsValue();
        if (!v.title || !v.message) { message.warning("Cần tiêu đề và nội dung"); return; }
        if (!v.path && !v.url) { message.warning("Chọn đường dẫn hoặc URL"); return; }
        if (!testUserId) { message.warning("Nhập User ID"); return; }
        try {
            setTestLoading(true);
            const { data } = await pushBroadcastApi.testSend({ ...buildRichOptions(v), title: v.title, message: v.message, path: v.path || undefined, url: v.url || undefined, userId: testUserId });
            message.success(data.data.queuedJobs > 0 ? "Đã gửi thử" : "User không có subscription đang bật");
            setTestOpen(false);
        } catch (err) {
            message.error(err?.response?.data?.message || "Gửi thử thất bại");
        } finally {
            setTestLoading(false);
        }
    };

    const handleSubmit = (values) => {
        if (!values.path && !values.url) { message.warning("Vui lòng chọn đường dẫn hoặc nhập URL"); return; }
        let scheduledAt = null;
        if (sendMode === "schedule") {
            if (!scheduleTime) { message.warning("Chọn thời gian gửi"); return; }
            if (scheduleTime.valueOf() <= Date.now()) { message.warning("Thời gian phải ở tương lai"); return; }
            scheduledAt = scheduleTime.valueOf();
        }
        const audience = getAudience();
        const whenText = scheduledAt ? `lúc ${scheduleTime.format("DD/MM/YYYY HH:mm")} (giờ máy bạn)` : "ngay bây giờ";
        Modal.confirm({
            title: "Xác nhận gửi push notification",
            content: (
                <>
                    <div>Gửi "{values.title}" đến {previewResult?.targetUserCount ?? "?"} users {whenText}?</div>
                    {deliveryMode === "topic" && (
                        <div className="mt-2 text-orange-600">
                            Gửi nhanh (Topic): tới mọi máy đã cài app, KHÔNG lọc được người nhận
                            và không đo được ai bấm.
                        </div>
                    )}
                </>
            ),
            okText: scheduledAt ? "Đặt lịch" : "Gửi ngay",
            okType: "danger",
            cancelText: "Hủy",
            onOk: async () => {
                try {
                    setSubmitLoading(true);
                    const payload = { ...buildPayload(values, audience, scheduledAt, deliveryMode), createdBy: auth.userInfo?.email || auth.userInfo?.name || "admin" };
                    const { data } = await pushBroadcastApi.broadcast(payload);
                    if (scheduledAt) { message.success("Đã đặt lịch gửi"); navigate("/push"); }
                    else if (data.data.broadcastId) { message.success("Đã gửi push notification"); navigate("/push/" + data.data.broadcastId); }
                    else { message.warning("Không có user phù hợp"); }
                } catch (err) {
                    message.error(err?.response?.data?.message || "Gửi thất bại");
                } finally {
                    setSubmitLoading(false);
                }
            },
        });
    };

    return (
        <div className="flex-1 p-6 overflow-auto" style={{ background: "rgb(var(--surface-sunken))" }}>
            <div className="flex items-center gap-3 mb-6">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/push")}>Quay lại</Button>
                <h1 className="text-xl font-semibold m-0">Tạo Push Notification</h1>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Row gutter={24}>
                    <Col xs={24} lg={15}>
                        <Card title={<span><FileTextOutlined className="mr-2" />Mẫu nội dung</span>} className="mb-4" style={{ borderRadius: 12 }}>
                            <PushTemplateControls onApply={(t) => form.setFieldsValue(contentToFields(t))} collectContent={collectContent} />
                        </Card>

                        <Card title={<span><EditOutlined className="mr-2" />Nội dung thông báo</span>} className="mb-4" style={{ borderRadius: 12 }}>
                            <Form.Item name="title" label="Tiêu đề" rules={[{ required: true, max: 120, message: "Tối đa 120 ký tự" }]}>
                                <Input placeholder="Tiêu đề thông báo" maxLength={120} showCount />
                            </Form.Item>
                            <Form.Item name="message" label="Nội dung" rules={[{ required: true, message: "Vui lòng nhập nội dung" }]}>
                                <TextArea rows={4} placeholder="Nội dung thông báo" />
                            </Form.Item>
                            <Form.Item label="Đường dẫn nhanh">
                                <Space wrap>
                                    {QUICK_PATHS.map(({ label, path }) => (
                                        <Button key={path} size="small" onClick={() => handleQuickPath(path)}>{label}</Button>
                                    ))}
                                </Space>
                            </Form.Item>
                            <Form.Item name="path" label="Custom path (app)"><Input placeholder="/vouchers" /></Form.Item>
                            <Form.Item name="url" label="URL (web)"><Input placeholder="https://..." /></Form.Item>
                            <PushRichOptions />
                        </Card>

                        <Card title={<span><TeamOutlined className="mr-2" />Đối tượng nhận</span>} className="mb-4" style={{ borderRadius: 12 }}>
                            <PushAudienceSelector
                                mode={audienceMode} onModeChange={handleAudienceModeChange}
                                selectedRoles={selectedRoles} onRolesChange={setSelectedRoles}
                                rawIds={rawIds} onRawIdsChange={setRawIds}
                                criteria={criteria} onCriteriaChange={handleCriteriaChange}
                            />
                            {audienceMode === "all" && (
                                <div className="mt-3">
                                    <div className="mb-1 font-medium">Cách gửi</div>
                                    <Segmented
                                        value={deliveryMode}
                                        onChange={setDeliveryMode}
                                        options={[
                                            { label: "Lần lượt (đo được)", value: "queue" },
                                            { label: "Nhanh — Topic", value: "topic" },
                                        ]}
                                    />
                                    <div className="mt-1 text-xs text-gray-500">
                                        {deliveryMode === "topic"
                                            ? "Một request tới Google, xong trong vài giây. Đổi lại: không đo được ai bấm, và bấm vào push không tự đánh dấu đã đọc. Dùng cho tin bắn đúng khung 0h."
                                            : "Xếp một lượt gửi cho mỗi người: khoảng 20 phút với 30.000 user, nhưng thống kê được từng người và tự dọn máy đã gỡ app."}
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center gap-3 mt-2">
                                <Button onClick={handlePreview} loading={previewLoading}>Xem trước số user</Button>
                                {previewResult !== null && (
                                    <span className="text-blue-600 font-medium">
                                        Sẽ gửi: <strong>{previewResult?.targetUserCount?.toLocaleString() ?? 0}</strong> user · <strong>{previewResult?.activeSubscriptionRows?.toLocaleString() ?? 0}</strong> subscription
                                    </span>
                                )}
                            </div>
                        </Card>

                        <Card title={<span><ClockCircleOutlined className="mr-2" />Thời gian gửi</span>} className="mb-4" style={{ borderRadius: 12 }}>
                            <Segmented
                                value={sendMode}
                                onChange={setSendMode}
                                options={[{ label: "Gửi ngay", value: "now" }, { label: "Hẹn giờ", value: "schedule" }]}
                            />
                            {sendMode === "schedule" && (
                                <div className="mt-3">
                                    <DatePicker
                                        showTime
                                        format="DD/MM/YYYY HH:mm"
                                        value={scheduleTime}
                                        onChange={setScheduleTime}
                                        disabledDate={(d) => d && d < dayjs().startOf("day")}
                                        placeholder="Chọn thời gian (giờ máy bạn)"
                                        style={{ width: 260 }}
                                    />
                                </div>
                            )}
                        </Card>

                        <Space>
                            <Button onClick={() => navigate("/push")}>Hủy</Button>
                            <Button icon={<ExperimentOutlined />} onClick={() => setTestOpen(true)}>Gửi thử</Button>
                            <Button type="primary" icon={sendMode === "schedule" ? <ClockCircleOutlined /> : <SendOutlined />} htmlType="submit" loading={submitLoading}>
                                {sendMode === "schedule" ? "Đặt lịch" : "Gửi thông báo"}
                            </Button>
                        </Space>
                    </Col>
                    <Col xs={24} lg={9}>
                        <div style={{ position: "sticky", top: 16 }}>
                            <PushLivePreview values={watched} />
                        </div>
                    </Col>
                </Row>
            </Form>

            <Modal title="Gửi thử tới 1 user" open={testOpen} onOk={handleTest} confirmLoading={testLoading} onCancel={() => setTestOpen(false)} okText="Gửi thử" cancelText="Hủy">
                <p className="text-content-3 text-sm mb-2">Gửi nội dung hiện tại tới đúng 1 User ID. Không lưu vào lịch sử chiến dịch.</p>
                <InputNumber min={1} style={{ width: "100%" }} placeholder="User ID" value={testUserId} onChange={setTestUserId} />
            </Modal>
        </div>
    );
}

export default PushBroadcastForm;
